import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Button,
  Checkbox,
  DatePicker,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
} from '@roger/ui-components';
import { WOUND_TYPES } from '../../constants/wounds';
import { getWoundAttachmentUrl, getWoundAttachments } from '../../services/api';
import type {
  WoundAttachmentResponse,
  WoundResponse,
} from '../../services/api';
import { useStore } from '../../store';
import WoundBodyMap, { type BodyMapPoint } from './WoundBodyMap';
import WoundTypeCombobox from './WoundTypeCombobox';
import { toDateStr } from './woundUtils';
import { parseISOToLocalDate } from '../../utils/dateUtils';

const DISCONTINUE_REASONS = [
  { value: 'wound_healed', label: 'Wound Healed' },
  { value: 'change_in_care', label: 'Change in Care' },
  { value: 'patient_refusal', label: 'Patient Refusal' },
  { value: 'other', label: 'Other' },
];

interface WoundFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingWound: WoundResponse | null;
  canDiscontinue?: boolean;
  onSave: (data: {
    location: string;
    wound_type: string;
    onset_date: string | null;
    body_map_point?: { x: number; y: number } | null;
    discontinued_date?: string | null;
    discontinued_reason?: string | null;
    discontinued_comments?: string | null;
  }) => Promise<void>;
}

const KNOWN_VALUES = new Set(WOUND_TYPES.map(t => t.value));

const WoundFormDialog: React.FC<WoundFormDialogProps> = ({
  open,
  onOpenChange,
  editingWound,
  canDiscontinue = false,
  onSave,
}) => {
  const [location, setLocation] = useState('');
  const [woundType, setWoundType] = useState('');
  const [otherDescription, setOtherDescription] = useState('');
  const [onsetDate, setOnsetDate] = useState<Date | undefined>(undefined);
  const [presentOnAdmission, setPresentOnAdmission] = useState(false);
  const [bodyMapPoint, setBodyMapPoint] = useState<BodyMapPoint | null>(null);
  const [discontinuedDate, setDiscontinuedDate] = useState<Date | undefined>(
    undefined
  );
  const [discontinuedReason, setDiscontinuedReason] = useState('');
  const [discontinuedReasonOther, setDiscontinuedReasonOther] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [woundImagePreviews, setWoundImagePreviews] = useState<
    { id: string; fileName: string; url: string }[]
  >([]);
  const [hasImageLoadError, setHasImageLoadError] = useState(false);

  const showError = useStore(state => state.showError);
  const imageErrorToastShownRef = useRef(false);

  useEffect(() => {
    if (open) {
      if (editingWound) {
        setLocation(editingWound.location);
        if (KNOWN_VALUES.has(editingWound.wound_type)) {
          setWoundType(editingWound.wound_type);
          setOtherDescription('');
        } else {
          setWoundType('other');
          setOtherDescription(editingWound.wound_type);
        }
        if (editingWound.onset_date) {
          setOnsetDate(parseISOToLocalDate(editingWound.onset_date));
          setPresentOnAdmission(false);
        } else {
          setOnsetDate(undefined);
          setPresentOnAdmission(true);
        }
        const bmp = (
          editingWound as WoundResponse & {
            body_map_point?: { x: number; y: number } | null;
          }
        ).body_map_point;
        setBodyMapPoint(bmp ?? null);
        setDiscontinuedDate(
          editingWound.discontinued_date
            ? parseISOToLocalDate(editingWound.discontinued_date)
            : undefined
        );
        const storedReason =
          (
            editingWound as WoundResponse & {
              discontinued_reason?: string | null;
            }
          ).discontinued_reason ?? '';
        if (storedReason === 'other') {
          setDiscontinuedReason('other');
          setDiscontinuedReasonOther(
            (
              editingWound as WoundResponse & {
                discontinued_comments?: string | null;
              }
            ).discontinued_comments ?? ''
          );
        } else {
          setDiscontinuedReason(storedReason);
          setDiscontinuedReasonOther('');
        }
      } else {
        setLocation('');
        setWoundType('');
        setOtherDescription('');
        setOnsetDate(undefined);
        setPresentOnAdmission(false);
        setBodyMapPoint(null);
        setDiscontinuedDate(undefined);
        setDiscontinuedReason('');
        setDiscontinuedReasonOther('');
      }
    }
  }, [open, editingWound]);

  useEffect(() => {
    let isCancelled = false;

    const loadWoundImages = async () => {
      if (!open || !editingWound) {
        setIsLoadingImages(false);
        setWoundImagePreviews([]);
        setHasImageLoadError(false);
        imageErrorToastShownRef.current = false;
        return;
      }

      setIsLoadingImages(true);
      setHasImageLoadError(false);
      try {
        const attachmentsResp = await getWoundAttachments({
          path: {
            patient_id: editingWound.patient_id,
            wound_id: editingWound.id,
          },
        });
        const attachments =
          (attachmentsResp.data as WoundAttachmentResponse[]) ?? [];
        const imageAttachments = attachments.filter(a =>
          a.mime_type.startsWith('image/')
        );
        let hadUrlFailure = false;
        const previewItems = await Promise.all(
          imageAttachments.map(async attachment => {
            try {
              const urlResp = await getWoundAttachmentUrl({
                path: {
                  patient_id: editingWound.patient_id,
                  wound_id: editingWound.id,
                  attachment_id: attachment.id,
                },
              });
              const data = urlResp.data as { url?: string };
              if (!data.url) {
                hadUrlFailure = true;
                return null;
              }
              return {
                id: attachment.id,
                fileName: attachment.file_name,
                url: data.url,
              };
            } catch (error) {
              console.error('Failed to load wound attachment URL:', error);
              hadUrlFailure = true;
              return null;
            }
          })
        );
        if (!isCancelled) {
          setWoundImagePreviews(
            previewItems.filter(
              (item): item is { id: string; fileName: string; url: string } =>
                item !== null
            )
          );
          if (hadUrlFailure) {
            setHasImageLoadError(true);
            if (!imageErrorToastShownRef.current) {
              imageErrorToastShownRef.current = true;
              showError(
                'Unable to load image preview',
                'Some wound images could not be displayed. Please refresh or try again.'
              );
            }
          }
        }
      } catch (error) {
        console.error('Failed to load wound attachments:', error);
        if (!isCancelled) {
          setWoundImagePreviews([]);
          setHasImageLoadError(true);
          if (!imageErrorToastShownRef.current) {
            imageErrorToastShownRef.current = true;
            showError(
              'Unable to load wound images',
              'Something went wrong. Please refresh or try again.'
            );
          }
        }
      } finally {
        if (!isCancelled) setIsLoadingImages(false);
      }
    };

    loadWoundImages();

    return () => {
      isCancelled = true;
    };
  }, [open, editingWound, showError]);

  const handleWoundTypeChange = (val: string) => {
    setWoundType(val);
    if (val !== 'other') setOtherDescription('');
  };

  const effectiveWoundType =
    woundType === 'other' ? otherDescription.trim() : woundType;

  const discontinuedValid =
    !discontinuedDate ||
    (!!discontinuedReason &&
      (discontinuedReason !== 'other' ||
        discontinuedReasonOther.trim().length > 0));

  const isValid =
    location.trim().length > 0 &&
    woundType.length > 0 &&
    (woundType !== 'other' || otherDescription.trim().length > 0) &&
    discontinuedValid;

  const handleSave = async () => {
    if (!isValid) return;
    setIsSaving(true);
    try {
      await onSave({
        location: location.trim(),
        wound_type: effectiveWoundType,
        onset_date: presentOnAdmission ? null : toDateStr(onsetDate),
        body_map_point: bodyMapPoint,
        ...(editingWound
          ? {
              discontinued_date: toDateStr(discontinuedDate),
              discontinued_reason: discontinuedReason || null,
              discontinued_comments:
                discontinuedReason === 'other'
                  ? discontinuedReasonOther.trim() || null
                  : null,
            }
          : {}),
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{editingWound ? 'Edit Wound' : 'Add Wound'}</DialogTitle>
        </DialogHeader>

        <div className='grid grid-cols-2 gap-6 py-2'>
          {/* Left column: fields */}
          <div className='space-y-4'>
            {/* Location */}
            <div className='space-y-1.5'>
              <Label>
                Location <span className='text-destructive'>*</span>
              </Label>
              <Input
                placeholder='e.g. Left heel, Sacrum'
                value={location}
                onChange={e => setLocation(e.target.value)}
              />
            </div>

            {/* Wound Type */}
            <div className='space-y-1.5'>
              <Label>
                Wound Type <span className='text-destructive'>*</span>
              </Label>
              <WoundTypeCombobox
                value={woundType}
                onValueChange={handleWoundTypeChange}
                onAddCustomType={text => {
                  setWoundType('other');
                  setOtherDescription(text);
                }}
              />
              {woundType === 'other' && (
                <Input
                  autoFocus
                  placeholder='Describe the wound type...'
                  value={otherDescription}
                  onChange={e => setOtherDescription(e.target.value)}
                />
              )}
            </div>

            {/* Onset Date */}
            <div className='space-y-1.5'>
              <Label>Onset Date</Label>
              <DatePicker
                date={onsetDate}
                onDateChange={setOnsetDate}
                placeholder='Select date'
                datePickerSize='default'
                disabled={presentOnAdmission}
              />
              <div className='flex items-center gap-2 mt-1'>
                <Checkbox
                  id='present-on-admission'
                  checked={presentOnAdmission}
                  onCheckedChange={checked => {
                    setPresentOnAdmission(!!checked);
                    if (checked) setOnsetDate(undefined);
                  }}
                />
                <label
                  htmlFor='present-on-admission'
                  className='text-sm text-muted-foreground cursor-pointer'
                >
                  Present upon admission
                </label>
              </div>
            </div>

            {/* Discontinued — edit mode + visit context only */}
            {editingWound && canDiscontinue && (
              <>
                <Separator />
                <div className='space-y-3'>
                  <Label className='text-sm font-medium text-muted-foreground uppercase tracking-wide'>
                    Discontinuation
                  </Label>
                  <div className='space-y-1.5'>
                    <Label>Date Discontinued</Label>
                    <DatePicker
                      date={discontinuedDate}
                      onDateChange={d => {
                        setDiscontinuedDate(d);
                        if (!d) {
                          setDiscontinuedReason('');
                          setDiscontinuedReasonOther('');
                        }
                      }}
                      placeholder='Select date'
                      datePickerSize='default'
                      showClearButton
                    />
                  </div>
                  {discontinuedDate && (
                    <>
                      <div className='space-y-1.5'>
                        <Label>Reason</Label>
                        <Select
                          value={discontinuedReason}
                          onValueChange={v => {
                            setDiscontinuedReason(v);
                            if (v !== 'other') setDiscontinuedReasonOther('');
                          }}
                        >
                          <SelectTrigger selectSize='sm'>
                            <SelectValue placeholder='Select reason...' />
                          </SelectTrigger>
                          <SelectContent>
                            {DISCONTINUE_REASONS.map(r => (
                              <SelectItem key={r.value} value={r.value}>
                                {r.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {discontinuedReason === 'other' && (
                        <div className='space-y-1.5'>
                          <Label>Please specify</Label>
                          <Input
                            placeholder='Describe reason...'
                            value={discontinuedReasonOther}
                            onChange={e =>
                              setDiscontinuedReasonOther(e.target.value)
                            }
                            inputSize='sm'
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Right column: body map */}
          <div className='space-y-1.5'>
            <Label>Body Location</Label>
            <WoundBodyMap value={bodyMapPoint} onChange={setBodyMapPoint} />
            {editingWound && (
              <div className='pt-2 space-y-1.5'>
                <Label>Uploaded Images</Label>
                {isLoadingImages ? (
                  <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                    <Loader2 className='h-3.5 w-3.5 animate-spin' />
                    Loading wound images...
                  </div>
                ) : woundImagePreviews.length === 0 ? (
                  <p className='text-xs text-muted-foreground'>
                    {hasImageLoadError
                      ? 'Unable to load wound images. Please refresh or try again.'
                      : 'No wound images uploaded yet.'}
                  </p>
                ) : (
                  <>
                    <div className='grid grid-cols-2 gap-2'>
                      {woundImagePreviews.slice(0, 4).map(image => (
                        <button
                          key={image.id}
                          type='button'
                          className='group text-left'
                          onClick={() => window.open(image.url, '_blank')}
                        >
                          <img
                            src={image.url}
                            alt={image.fileName}
                            className='h-24 w-full rounded-md border object-cover'
                          />
                          <p className='mt-1 text-xs text-muted-foreground truncate'>
                            {image.fileName}
                          </p>
                        </button>
                      ))}
                    </div>
                    {hasImageLoadError && (
                      <p className='text-xs text-destructive'>
                        Some wound images could not be displayed. Please refresh
                        or try again.
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!isValid || isSaving}>
            {isSaving && <Loader2 className='h-4 w-4 mr-1 animate-spin' />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WoundFormDialog;
